import React, { useState } from 'react';
import { Search, Loader2, AlertTriangle } from 'lucide-react';

import { CodeProfile } from '../types';
import { BackendAPI } from '../lib/api';

import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/Card';

// ================================================================= //
//          Code Lookup Tab                                            //
// ================================================================= //

const CodeLookupTab: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [result, setResult] = useState<CodeProfile | null>(null);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setLookupError(null);
    setSearched(true);
    try {
      const profile = await BackendAPI.getCodeProfile(searchQuery.trim());
      setResult(profile);
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : 'Code not found');
      setResult(null);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Xactimate Code Lookup</CardTitle>
          <CardDescription>
            Search for any Xactimate code to see its profile and usage patterns.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter Xactimate code (e.g., DRY INST)"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} isLoading={searching}>
              <Search className="-ml-1 mr-2 h-4 w-4" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {searching && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {lookupError && searched && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-4">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <p className="text-sm text-red-800">{lookupError}</p>
        </div>
      )}

      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="font-mono text-primary">{result.xactimate_code}</span>
              <span className="text-foreground">{result.primary_description}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Category</dt>
                <dd className="mt-1 text-sm text-foreground">{result.category} {result.selector}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Unit</dt>
                <dd className="mt-1 font-mono text-sm text-foreground">{result.primary_unit}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Occurrences</dt>
                <dd className="mt-1 text-sm text-foreground">{result.occurrence_count} across {result.estimate_count} estimates</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Avg Quantity</dt>
                <dd className="mt-1 font-mono text-sm text-foreground">{result.avg_quantity != null ? result.avg_quantity.toFixed(1) : '—'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Primary Activity</dt>
                <dd className="mt-1 text-sm text-foreground">{result.primary_activity}</dd>
              </div>
            </dl>

            {Object.keys(result.descriptions).length > 1 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-muted-foreground">Description Variants</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {Object.entries(result.descriptions).slice(0, 5).map(([desc, count]) => (
                    <span key={desc} className="rounded-full border bg-white px-2 py-0.5 text-xs">{desc} ({count})</span>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(result.activities).length > 1 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-muted-foreground">Activity Types</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {Object.entries(result.activities).map(([act, count]) => (
                    <span key={act} className="rounded-full border bg-primary/5 px-2 py-0.5 font-mono text-xs text-primary">{act} ({count})</span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!result && searched && !searching && !lookupError && (
        <p className="py-4 text-center text-muted-foreground">No profile found for that code.</p>
      )}
    </div>
  );
};

export default CodeLookupTab;
